import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Download, Smartphone, X, Check } from 'lucide-react';
import { usePushNotifications, usePWA, useServiceWorker } from '../hooks/usePWA';

const PWAInstallPrompt = () => {
  const { isInstallable, isInstalled, install } = usePWA();
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe } = usePushNotifications();
  const { updateAvailable, update } = useServiceWorker();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Show banner after 5 seconds if not installed and not dismissed
    const dismissed = localStorage.getItem('pwa_banner_dismissed');
    if (!isInstalled && !dismissed) {
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [isInstalled]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_banner_dismissed', 'true');
  };

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      setShowBanner(false);
    }
  };

  const handleNotificationToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  // Update available banner
  if (updateAvailable) {
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-blue-600 text-white rounded-lg shadow-lg p-4 z-50 animate-slide-up">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold">Actualización Disponible</h4>
            <p className="text-sm text-blue-100 mt-1">
              Hay una nueva versión de NotaryOS disponible.
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={update}
            className="flex-1 bg-white text-blue-600 font-medium py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Actualizar Ahora
          </button>
        </div>
      </div>
    );
  }

  // Install banner for mobile
  if (showBanner && isInstallable) {
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4 z-50 animate-slide-up">
        <button
          onClick={dismissBanner}
          className="absolute top-3 right-3 text-slate-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 pr-6">
            <h4 className="font-semibold text-white">Instalar NotaryOS</h4>
            <p className="text-sm text-slate-400 mt-1">
              Accede rápidamente desde tu pantalla de inicio y trabaja sin conexión.
            </p>
          </div>
        </div>
        
        <div className="flex gap-3 mt-4">
          <button
            onClick={dismissBanner}
            className="flex-1 text-slate-400 font-medium py-2.5 px-4 rounded-lg hover:bg-slate-700/50 transition-colors"
          >
            Ahora no
          </button>
          <button
            onClick={handleInstall}
            className="flex-1 bg-blue-600 text-white font-medium py-2.5 px-4 rounded-lg hover:bg-blue-500 transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Instalar
          </button>
        </div>
      </div>
    );
  }

  // Settings panel (shown when clicking notification bell in header)
  if (showSettings) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h3 className="font-semibold text-white">Configuración de App</h3>
            <button
              onClick={() => setShowSettings(false)}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* PWA Install */}
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Smartphone className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="text-white font-medium">Aplicación Instalada</div>
                  <div className="text-xs text-slate-400">
                    {isInstalled ? 'NotaryOS está instalado' : 'Instalar en tu dispositivo'}
                  </div>
                </div>
              </div>
              {isInstalled ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : isInstallable ? (
                <button
                  onClick={handleInstall}
                  className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-500"
                >
                  Instalar
                </button>
              ) : (
                <span className="text-xs text-slate-500">No disponible</span>
              )}
            </div>

            {/* Push Notifications */}
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                {isSubscribed ? (
                  <Bell className="w-5 h-5 text-green-400" />
                ) : (
                  <BellOff className="w-5 h-5 text-slate-400" />
                )}
                <div>
                  <div className="text-white font-medium">Notificaciones Push</div>
                  <div className="text-xs text-slate-400">
                    {isSubscribed ? 'Activadas' : 'Recibe alertas legales importantes'}
                  </div>
                </div>
              </div>
              {isSupported ? (
                <button
                  onClick={handleNotificationToggle}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    isSubscribed ? 'bg-green-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      isSubscribed ? 'translate-x-6' : ''
                    }`}
                  />
                </button>
              ) : (
                <span className="text-xs text-slate-500">No soportado</span>
              )}
            </div>

            {permission === 'denied' && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-sm text-red-400">
                  Las notificaciones están bloqueadas. Habilítalas en la configuración de tu navegador.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Export a button component to trigger settings
export const PWASettingsButton = ({ onClick }) => {
  const { isSubscribed } = usePushNotifications();
  
  return (
    <button
      onClick={onClick}
      className="relative p-2 text-slate-400 hover:text-white transition-colors"
      title="Configuración de notificaciones"
    >
      {isSubscribed ? (
        <Bell className="w-5 h-5" />
      ) : (
        <BellOff className="w-5 h-5" />
      )}
      {isSubscribed && (
        <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
      )}
    </button>
  );
};

export default PWAInstallPrompt;
